import React, { useCallback, useMemo, useState } from 'react';
import { PackCard } from '../components/PackCard';
type Language = 'es' | 'en';
type Category = 'profesionales' | 'estudiantes';
interface Pack {
  title: Record<Language, string>;
  description: Record<Language, string>;
  packTitle: string;
  themeColor: 'blue' | 'green' | 'pink' | 'purple' | 'teal' | 'navy';
  imageSrc: Record<Language, string>;
  href: Record<Language, string>;
  category: Category;
}
interface ElegirPackProps {
  language: Language;
  onLanguageToggle: () => void;
}
const pageCopy = {
  es: {
    languageName: 'Cambiar a inglés',
    badge: 'Packs digitales para profesionales y estudiantes',
    heading: '¿Qué tipo de material buscas?',
    description: 'Elegí tu pack y accedé a recursos listos para usar, imprimir y ahorrar horas de tu vida.',
    professional: 'Profesionales',
    students: 'Estudiantes',
    filterLabel: 'Filtrar por categoría',
    sectionLabel: 'Sistemas para',
    ctaLabel: 'Quiero mi sistema ahora',
    viewLabel: 'Ver sistema de',
    buyLabel: 'Comprar sistema de'
  },
  en: {
    languageName: 'Switch to Spanish',
    badge: 'Digital packs for professionals and students',
    heading: 'What type of material are you looking for?',
    description: 'Choose your pack and access ready-to-use resources to print, use and save hours of your time.',
    professional: 'Professionals',
    students: 'Students',
    filterLabel: 'Filter by category',
    sectionLabel: 'Systems for',
    ctaLabel: 'Get my system now',
    viewLabel: 'View system for',
    buyLabel: 'Buy system for'
  }
} as const;
// Replace each link below with the matching pack image and destination.
// `es` is the Spanish version and `en` is the English version.
const packs: Pack[] = [{
  title: {
    es: 'Nutrición',
    en: 'Nutrition'
  },
  description: {
    es: 'Sistema completo para nutricionistas: planes, recetas, seguimiento y educación alimentaria.',
    en: 'A complete system for nutritionists: plans, recipes, follow-up resources and nutrition education.'
  },
  packTitle: 'NUTRICIÓN LISTA',
  themeColor: 'navy',
  imageSrc: {
    es: 'https://res.cloudinary.com/dowzlbqlz/image/upload/v1783951102/ChatGPT_Image_13_jul_2026_10_58_09_erdy8k.png',
    en: 'https://res.cloudinary.com/dowzlbqlz/image/upload/v1783949996/ChatGPT_Image_13_jul_2026_10_39_48_se1jfw.png'
  },
  href: {
    es: 'https://nutricionresuelta.impultienda.ar/',
    en: 'https://nutritionsolved.impultienda.ar/'
  },
  category: 'profesionales'
}, {
  title: {
    es: 'Kinesiología',
    en: 'Kinesiology'
  },
  description: {
    es: 'Protocolos, sesiones y materiales listos para trabajar con pacientes.',
    en: 'Protocols, sessions and ready-to-use materials for working with patients.'
  },
  packTitle: 'FONO PRO',
  themeColor: 'blue',
  imageSrc: {
    es: 'https://res.cloudinary.com/dowzlbqlz/image/upload/v1783948355/4f5610f6-ae1a-481c-af42-7996860328a5-mockup_hmb4fc.png',
    en: 'https://res.cloudinary.com/dowzlbqlz/image/upload/v1783948354/3d622424-45e6-4f7f-8e47-29f9ee49cc2a-mockup_ecuffl.jpg'
  },
  href: {
    es: 'https://kinesiologiaresuelta.impultienda.ar/',
    en: 'https://physiopractice.impultienda.ar/'
  },
  category: 'profesionales'
}, {
  title: {
    es: 'Psicología',
    en: 'Psychology'
  },
  description: {
    es: 'Resúmenes, guías y materiales para organizar mejor la carrera de Psicología.',
    en: 'Summaries, guides and resources to organize your Psychology degree.'
  },
  packTitle: 'TERAPIA OCUPACIONAL',
  themeColor: 'teal',
  imageSrc: {
    es: 'https://res.cloudinary.com/dowzlbqlz/image/upload/v1783940282/ChatGPT_Image_12_jul_2026_13_06_02_3_1_ttnrno.png',
    en: 'https://res.cloudinary.com/dowzlbqlz/image/upload/v1783940273/hf_20260709_013626_6c8f6829-209e-46bb-b39a-a94138b4b5f8_y2svyf.png'
  },
  href: {
    es: 'https://psicologiaresuelta.impultienda.ar/',
    en: 'https://psychologysolved.impultienda.ar/'
  },
  category: 'estudiantes'
}];
export function ElegirPack({
  language,
  onLanguageToggle
}: ElegirPackProps) {
  const [selectedCategory, setSelectedCategory] = useState<Category>('profesionales');
  const copy = pageCopy[language];
  const filteredPacks = useMemo(() => packs.filter(pack => pack.category === selectedCategory), [selectedCategory]);
  const selectProfesionales = useCallback(() => setSelectedCategory('profesionales'), []);
  const selectEstudiantes = useCallback(() => setSelectedCategory('estudiantes'), []);
  const categoryName = selectedCategory === 'profesionales' ? copy.professional : copy.students;
  return <main className="relative min-h-screen bg-[#F6F5F2] py-16 px-4 sm:px-6 lg:px-8 font-sans selection:bg-green-200 selection:text-green-900" data-id="element-40">
      <button type="button" onClick={onLanguageToggle} className="absolute left-4 top-4 sm:left-6 sm:top-6 w-11 h-11 rounded-full bg-white border border-black/10 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A7D3B] focus-visible:ring-offset-2" aria-label={copy.languageName} title={copy.languageName} data-id="element-41">
        {language === 'es' ? <svg aria-hidden="true" viewBox="0 0 30 20" className="w-6 h-4 rounded-sm shadow-sm" data-id="element-42">
            <rect width="30" height="20" fill="#AA151B" data-id="element-43" />
            <rect y="5" width="30" height="10" fill="#F1BF00" data-id="element-44" />
            <rect x="5" y="7.5" width="3" height="5" fill="#AA151B" data-id="element-45" />
          </svg> : <svg aria-hidden="true" viewBox="0 0 30 20" className="w-6 h-4 rounded-sm shadow-sm" data-id="element-46">
            <rect width="30" height="20" fill="#B22234" data-id="element-47" />
            <path d="M0 1.54h30M0 4.62h30M0 7.69h30M0 10.77h30M0 13.85h30M0 16.92h30" stroke="#fff" strokeWidth="1.54" data-id="element-48" />
            <rect width="13" height="10.77" fill="#3C3B6E" data-id="element-49" />
            <g fill="#fff" data-id="element-50">
              <circle cx="2.2" cy="2" r=".55" data-id="element-51" />
              <circle cx="5.2" cy="2" r=".55" data-id="element-52" />
              <circle cx="8.2" cy="2" r=".55" data-id="element-53" />
              <circle cx="11.2" cy="2" r=".55" data-id="element-54" />
              <circle cx="3.7" cy="4" r=".55" data-id="element-55" />
              <circle cx="6.7" cy="4" r=".55" data-id="element-56" />
              <circle cx="9.7" cy="4" r=".55" data-id="element-57" />
              <circle cx="2.2" cy="6" r=".55" data-id="element-58" />
              <circle cx="5.2" cy="6" r=".55" data-id="element-59" />
              <circle cx="8.2" cy="6" r=".55" data-id="element-60" />
              <circle cx="11.2" cy="6" r=".55" data-id="element-61" />
              <circle cx="3.7" cy="8" r=".55" data-id="element-62" />
              <circle cx="6.7" cy="8" r=".55" data-id="element-63" />
              <circle cx="9.7" cy="8" r=".55" data-id="element-64" />
            </g>
          </svg>}
      </button>

      <div className="max-w-[1400px] mx-auto" data-id="element-65">
        <header className="flex flex-col items-center text-center mb-16" data-id="element-66">
          <p className="bg-[#E5F7EB] text-[#0A7D3B] text-xs font-bold px-4 py-1.5 rounded-full tracking-wider uppercase mb-6" data-id="element-67">
            {copy.badge}
          </p>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#111111] tracking-tight mb-6" data-id="element-68">
            {copy.heading}
          </h1>

          <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-8" data-id="element-69">
            {copy.description}
          </p>

          <nav aria-label={copy.filterLabel} className="flex flex-wrap items-center justify-center gap-3" data-id="element-70">
            <button type="button" onClick={selectProfesionales} aria-pressed={selectedCategory === 'profesionales'} className={`px-6 lg:px-8 py-3 rounded-full font-semibold text-base leading-none transition-all duration-150 min-w-[170px] ${selectedCategory === 'profesionales' ? 'bg-[#0A7D3B] text-white shadow-[0_5px_0_0_#065F2C]' : 'bg-white text-[#0A7D3B] border border-[#0A7D3B]'}`} data-id="element-71">
              {copy.professional}
            </button>

            <button type="button" onClick={selectEstudiantes} aria-pressed={selectedCategory === 'estudiantes'} className={`px-6 lg:px-8 py-3 rounded-full font-semibold text-base leading-none transition-all duration-150 min-w-[170px] ${selectedCategory === 'estudiantes' ? 'bg-[#0A7D3B] text-white shadow-[0_5px_0_0_#065F2C]' : 'bg-white text-[#0A7D3B] border border-[#0A7D3B]'}`} data-id="element-72">
              {copy.students}
            </button>
          </nav>
        </header>

        <section aria-label={`${copy.sectionLabel} ${categoryName}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center" data-id="element-73">
          {filteredPacks.map((pack, index) => <PackCard key={`${pack.category}-${pack.title[language]}-${index}`} title={pack.title[language]} description={pack.description[language]} packTitle={pack.packTitle} themeColor={pack.themeColor} imageSrc={pack.imageSrc[language]} href={pack.href[language]} delay={0.05 + index * 0.05} priority={index < 3} ctaLabel={copy.ctaLabel} viewLabel={copy.viewLabel} buyLabel={copy.buyLabel} data-id="element-74" />)}
        </section>
      </div>
    </main>;
}